import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft, Play, Pause, Plus, Trash2, Download,
  Type, Sliders, Music, Scissors, X, GripHorizontal,
  MoveHorizontal, Type as TypeIcon, Palette, Sparkles,
  Subtitles, Captions, Clapperboard,
} from "lucide-react";
import styles from "~/styles/quickcut.module.css";

// ── TYPES ─────────────────────────────────────────────────────────────────────

interface TextClip {
  id: string;
  text: string;
  start: number;   // seconds
  end: number;     // seconds
  x: number;       // % of container width
  y: number;       // % of container height
  size: number;    // px in display view
  color: string;
  opacity: number; // 0-100
  fontFamily: string;
  animation: "none" | "fade" | "slideUp" | "typewriter";
}

type Tab = "trim" | "text" | "subtitles" | "filters" | "adjust" | "audio";
type TextTool = "move" | "font" | "color" | "animation";

export interface QuickCutProps {
  blob: Blob;
  speechSegments?: { text: string; startMs: number; endMs: number }[];
  onBack: () => void;
}

// ── HELPERS ───────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 8);

interface FilterPreset {
  id: string;
  name: string;
  filter: string;
  icon: string;
}

const FILTER_PRESETS: FilterPreset[] = [
  { id: "original", name: "Original", filter: "", icon: "✦" },
  { id: "noir", name: "Noir", filter: "grayscale(100%) contrast(140%) brightness(90%)", icon: "◐" },
  { id: "sepia", name: "Vintage", filter: "sepia(70%) contrast(110%) brightness(105%) saturate(85%)", icon: "☀" },
  { id: "cyberpunk", name: "Cyber", filter: "hue-rotate(200deg) saturate(180%) contrast(125%) brightness(105%)", icon: "⚡" },
  { id: "warm", name: "Cálido", filter: "sepia(25%) saturate(130%) hue-rotate(-10deg) brightness(102%)", icon: "🔥" },
  { id: "cool", name: "Frío", filter: "hue-rotate(-25deg) saturate(75%) brightness(108%) contrast(110%)", icon: "❄" },
  { id: "contrast", name: "Drama", filter: "contrast(160%) saturate(130%) brightness(95%)", icon: "◼" },
  { id: "faded", name: "Suave", filter: "contrast(80%) brightness(115%) saturate(70%) sepia(10%)", icon: "☁" },
  { id: "vivid", name: "Vívido", filter: "saturate(220%) contrast(115%) brightness(102%)", icon: "✺" },
  { id: "night", name: "Nocturno", filter: "hue-rotate(100deg) saturate(180%) contrast(140%) brightness(80%)", icon: "◉" },
  { id: "dramatic", name: "Cine", filter: "contrast(130%) brightness(85%) saturate(110%) sepia(15%)", icon: "🎬" },
  { id: "golden", name: "Dorado", filter: "sepia(40%) saturate(140%) hue-rotate(-15deg) contrast(110%)", icon: "✧" },
];

const FONT_OPTIONS = [
  { label: "Ioskeley Mono", value: "var(--font-share-tech-mono), monospace", canvasName: "'IoskeleyMono', monospace" },
  { label: "Inter", value: "var(--font-inter), sans-serif", canvasName: "'Inter', sans-serif" },
  { label: "Space Grotesk", value: "var(--font-space-grotesk), sans-serif", canvasName: "'Space Grotesk', sans-serif" },
  { label: "Poppins", value: "var(--font-poppins), sans-serif", canvasName: "'Poppins', sans-serif" },
  { label: "Bebas Neue", value: "var(--font-bebas-neue), sans-serif", canvasName: "'Bebas Neue', sans-serif" },
  { label: "Rajdhani", value: "var(--font-rajdhani), sans-serif", canvasName: "'Rajdhani', sans-serif" },
  { label: "Exo 2", value: "var(--font-exo-2), sans-serif", canvasName: "'Exo 2', sans-serif" },
  { label: "JetBrains Mono", value: "var(--font-jetbrains-mono), monospace", canvasName: "'JetBrains Mono', monospace" },
  { label: "Orbitron", value: "var(--font-orbitron), sans-serif", canvasName: "'Orbitron', sans-serif" },
] as const;

function getCanvasFontName(cssFontValue: string): string {
  const found = FONT_OPTIONS.find((f) => f.value === cssFontValue);
  return found?.canvasName ?? "'IoskeleyMono', monospace";
}

const fmt = (s: number) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 10);
  return `${m}:${sec.toString().padStart(2, "0")}.${ms}`;
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const hasVFCB = typeof window !== "undefined" &&
  "requestVideoFrameCallback" in HTMLVideoElement.prototype;

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export function QuickCut({ blob, speechSegments = [], onBack }: QuickCutProps) {
  const videoRef         = useRef<HTMLVideoElement>(null);
  const containerRef     = useRef<HTMLDivElement>(null);
  const timelineRef      = useRef<HTMLDivElement>(null);
  const timelineTrackRef = useRef<HTMLDivElement>(null);
  const exportCancelRef  = useRef(false);
  const blobUrlRef       = useRef("");
  const trimDragRef      = useRef<"start" | "end" | null>(null);
  const textDragRef      = useRef<{
    id: string; startPX: number; startPY: number; origX: number; origY: number;
  } | null>(null);
  const timelineDragRef  = useRef<{
    type: "scrub" | "clip" | "clipStart" | "clipEnd";
    clipId?: string;
    startX: number;
    origStart?: number;
    origEnd?: number;
  } | null>(null);

  // ── Video state ─────────────────────────────────────────────────────────────
  const [duration,    setDuration]    = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying,   setIsPlaying]   = useState(false);

  // ── Trim ────────────────────────────────────────────────────────────────────
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd,   setTrimEnd]   = useState(0);

  // ── Text clips ──────────────────────────────────────────────────────────────
  const [textClips,    setTextClips]    = useState<TextClip[]>([]);
  const [selectedClip, setSelectedClip] = useState<string | null>(null);
  const [textTool,     setTextTool]     = useState<TextTool>("move");

  // ── Image adjustments ───────────────────────────────────────────────────────
  const [brightness,  setBrightness]  = useState(100);
  const [contrast,    setContrast]    = useState(100);
  const [saturation,  setSaturation]  = useState(100);
  const [hueRotate,   setHueRotate]   = useState(0);
  const [blur,        setBlur]        = useState(0);
  const [sepia,       setSepia]       = useState(0);
  const [grayscale,   setGrayscale]   = useState(0);
  const [invert,      setInvert]      = useState(0);
  const [videoOpacity, setVideoOpacity] = useState(100);
  const [activeFilter, setActiveFilter] = useState<string>("original");

  // ── Audio ───────────────────────────────────────────────────────────────────
  const [audioFile,   setAudioFile]   = useState<File | null>(null);
  const [videoVolume, setVideoVolume] = useState(100);
  const [bgVolume,    setBgVolume]    = useState(80);

  // ── UI ──────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<Tab>("trim");
  const [timelineZoom, setTimelineZoom] = useState(1);

  // ── Export ──────────────────────────────────────────────────────────────────
  const [isExporting,    setIsExporting]    = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportUrl,      setExportUrl]      = useState("");
  const [exportDone,     setExportDone]     = useState(false);

  const hasChanges = useMemo(() =>
    trimStart > 0.05 ||
    (duration > 0 && trimEnd < duration - 0.05) ||
    textClips.length > 0 ||
    brightness !== 100 || contrast !== 100 || saturation !== 100 ||
    hueRotate !== 0 || blur !== 0 || sepia !== 0 || grayscale !== 0 ||
    invert !== 0 || videoOpacity !== 100 ||
    activeFilter !== "original" ||
    audioFile !== null || videoVolume !== 100,
  [trimStart, duration, trimEnd, textClips.length, brightness, contrast, saturation, hueRotate, blur, sepia, grayscale, invert, videoOpacity, activeFilter, audioFile, videoVolume]);

  const filterStr = useMemo(() => {
    const preset = FILTER_PRESETS.find((p) => p.id === activeFilter)?.filter ?? "";
    return [
      `brightness(${brightness}%)`,
      `contrast(${contrast}%)`,
      `saturate(${saturation}%)`,
      `hue-rotate(${hueRotate}deg)`,
      `blur(${blur}px)`,
      `sepia(${sepia}%)`,
      `grayscale(${grayscale}%)`,
      `invert(${invert}%)`,
      preset,
    ].filter(Boolean).join(" ").trim();
  }, [brightness, contrast, saturation, hueRotate, blur, sepia, grayscale, invert, activeFilter]);

  const selectedLayer = useMemo(() => textClips.find((l) => l.id === selectedClip), [textClips, selectedClip]);

  // ── Load video ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    const video = videoRef.current;
    if (!video) return;
    video.src = url;
    video.load();
    const onMeta = () => {
      setDuration(video.duration);
      setTrimEnd(video.duration);
      setCurrentTime(0);
    };
    video.addEventListener("loadedmetadata", onMeta);
    return () => {
      video.removeEventListener("loadedmetadata", onMeta);
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  // ── Time tracking ────────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      setCurrentTime(video.currentTime);
      if (video.currentTime >= trimEnd) {
        video.pause();
        video.currentTime = trimStart;
        setIsPlaying(false);
      }
    };
    video.addEventListener("timeupdate", onTime);
    return () => video.removeEventListener("timeupdate", onTime);
  }, [trimStart, trimEnd]);

  // ── Export URL cleanup ───────────────────────────────────────────────────────
  useEffect(() => () => { if (exportUrl) URL.revokeObjectURL(exportUrl); }, [exportUrl]);

  // ── Play / Pause ─────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.currentTime >= trimEnd) v.currentTime = trimStart;
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  }, [trimStart, trimEnd]);

  // ── Timeline helpers ─────────────────────────────────────────────────────────
  const timeToPx = useCallback((t: number) => {
    if (!timelineTrackRef.current || duration <= 0) return 0;
    const rect = timelineTrackRef.current.getBoundingClientRect();
    return (t / duration) * rect.width * timelineZoom;
  }, [duration, timelineZoom]);

  const pxToTime = useCallback((px: number) => {
    if (!timelineTrackRef.current || duration <= 0) return 0;
    const rect = timelineTrackRef.current.getBoundingClientRect();
    return (px / (rect.width * timelineZoom)) * duration;
  }, [duration, timelineZoom]);

  // ── Trim drag ────────────────────────────────────────────────────────────────
  const startTrimDrag = (e: React.PointerEvent, handle: "start" | "end") => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    trimDragRef.current = handle;
  };

  const onHandleMove = (e: React.PointerEvent, handle: "start" | "end") => {
    if (trimDragRef.current !== handle || !timelineTrackRef.current) return;
    const rect = timelineTrackRef.current.getBoundingClientRect();
    const scrollLeft = timelineTrackRef.current.scrollLeft;
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left + scrollLeft) / (rect.width * timelineZoom)));
    const t = pct * duration;
    if (handle === "start") {
      const next = Math.min(t, trimEnd - 0.3);
      setTrimStart(next);
      if (videoRef.current) videoRef.current.currentTime = next;
    } else {
      const next = Math.max(t, trimStart + 0.3);
      setTrimEnd(next);
      if (videoRef.current) videoRef.current.currentTime = next;
    }
  };

  const endTrimDrag = () => { trimDragRef.current = null; };

  // ── Playhead / Timeline scrub ───────────────────────────────────────────────
  const onTimelinePointerDown = (e: React.PointerEvent) => {
    if (!timelineTrackRef.current) return;
    e.preventDefault();
    timelineTrackRef.current.setPointerCapture(e.pointerId);
    const rect = timelineTrackRef.current.getBoundingClientRect();
    const scrollLeft = timelineTrackRef.current.scrollLeft;
    const t = pxToTime(e.clientX - rect.left + scrollLeft);
    const clamped = clamp(t, trimStart, trimEnd);
    setCurrentTime(clamped);
    if (videoRef.current) videoRef.current.currentTime = clamped;
    timelineDragRef.current = { type: "scrub", startX: e.clientX };
  };

  const onTimelinePointerMove = (e: React.PointerEvent) => {
    if (!timelineDragRef.current || !timelineTrackRef.current) return;
    const rect = timelineTrackRef.current.getBoundingClientRect();
    const scrollLeft = timelineTrackRef.current.scrollLeft;
    const t = pxToTime(e.clientX - rect.left + scrollLeft);
    const clamped = clamp(t, 0, duration);
    if (timelineDragRef.current.type === "scrub") {
      setCurrentTime(clamped);
      if (videoRef.current) videoRef.current.currentTime = clamped;
    }
  };

  const onTimelinePointerUp = () => {
    timelineDragRef.current = null;
  };

  // ── Text clip drag on timeline ──────────────────────────────────────────────
  const startClipDrag = (e: React.PointerEvent, clip: TextClip, mode: "clip" | "clipStart" | "clipEnd") => {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setSelectedClip(clip.id);
    timelineDragRef.current = {
      type: mode,
      clipId: clip.id,
      startX: e.clientX,
      origStart: clip.start,
      origEnd: clip.end,
    };
  };

  const onClipDragMove = (e: React.PointerEvent) => {
    if (!timelineDragRef.current || !timelineTrackRef.current) return;
    const { type, clipId, startX, origStart = 0, origEnd = 0 } = timelineDragRef.current;
    const rect = timelineTrackRef.current.getBoundingClientRect();
    const scrollLeft = timelineTrackRef.current.scrollLeft;
    const deltaT = pxToTime(e.clientX - rect.left + scrollLeft) - pxToTime(startX - rect.left + scrollLeft);

    setTextClips((prev) => prev.map((c) => {
      if (c.id !== clipId) return c;
      if (type === "clip") {
        const duration = c.end - c.start;
        const newStart = clamp(origStart + deltaT, 0, Math.max(0, (origEnd + deltaT) - duration));
        const newEnd = newStart + duration;
        return { ...c, start: newStart, end: Math.min(newEnd, duration) };
      }
      if (type === "clipStart") {
        return { ...c, start: clamp(origStart + deltaT, 0, c.end - 0.2) };
      }
      if (type === "clipEnd") {
        return { ...c, end: clamp(origEnd + deltaT, c.start + 0.2, duration) };
      }
      return c;
    }));
  };

  const endClipDrag = () => { timelineDragRef.current = null; };

  // ── Text overlay drag on video ───────────────────────────────────────────────
  const startTextDrag = (
    e: React.PointerEvent,
    id: string, origX: number, origY: number,
  ) => {
    if (textTool !== "move") return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setSelectedClip(id);
    textDragRef.current = { id, startPX: e.clientX, startPY: e.clientY, origX, origY };
  };

  const onTextMove = (e: React.PointerEvent) => {
    if (!textDragRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const { id, startPX, startPY, origX, origY } = textDragRef.current;
    const dx = ((e.clientX - startPX) / rect.width) * 100;
    const dy = ((e.clientY - startPY) / rect.height) * 100;
    setTextClips((prev) => prev.map((c) =>
      c.id === id
        ? { ...c, x: Math.max(0, Math.min(90, origX + dx)), y: Math.max(2, Math.min(95, origY + dy)) }
        : c,
    ));
  };

  const endTextDrag = () => { textDragRef.current = null; };

  // ── Text clip CRUD ──────────────────────────────────────────────────────────
  const addTextClip = () => {
    const start = currentTime;
    const end = Math.min(start + 3, duration || 10);
    const layer: TextClip = {
      id: uid(),
      text: "Texto",
      start,
      end,
      x: 10,
      y: 50,
      size: 26,
      color: "#ffffff",
      opacity: 100,
      fontFamily: "var(--font-share-tech-mono), monospace",
      animation: "none",
    };
    setTextClips((prev) => [...prev, layer]);
    setSelectedClip(layer.id);
    setActiveTab("text");
  };

  const updateTextClip = (id: string, patch: Partial<TextClip>) =>
    setTextClips((prev) => prev.map((c) => c.id === id ? { ...c, ...patch } : c));

  const removeTextClip = (id: string) => {
    setTextClips((prev) => prev.filter((c) => c.id !== id));
    if (selectedClip === id) setSelectedClip(null);
  };

  // ── Generate subtitles from speech segments ─────────────────────────────────
  const generateSubtitlesFromSpeech = () => {
    if (!speechSegments.length) return;
    const newClips: TextClip[] = speechSegments.map((seg, i) => {
      const yPos = 75 + ((i % 3) * 8); // stagger vertically to avoid overlap
      return {
        id: uid(),
        text: seg.text,
        start: seg.startMs / 1000,
        end: seg.endMs / 1000,
        x: 10,
        y: yPos,
        size: 22,
        color: "#ffffff",
        opacity: 100,
        fontFamily: "var(--font-share-tech-mono), monospace",
        animation: "none",
      };
    });
    setTextClips((prev) => [...prev, ...newClips]);
    setActiveTab("text");
  };

  const hasSubtitles = textClips.length > 0;

  // ── Active clips at current time ────────────────────────────────────────────
  const activeClips = textClips.filter((c) => currentTime >= c.start && currentTime <= c.end);

  // ── Export ───────────────────────────────────────────────────────────────────
  const startExport = async () => {
    const video = videoRef.current;
    if (!video || isExporting) return;
    setIsExporting(true);
    setExportProgress(0);
    exportCancelRef.current = false;

    let audioCtx: AudioContext | null = null;
    try {
      const W = video.videoWidth || 1280;
      const H = video.videoHeight || 720;

      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d")!;

      audioCtx = new AudioContext();
      const streamDest = audioCtx.createMediaStreamDestination();
      const videoSrc = audioCtx.createMediaElementSource(video);
      const videoGain = audioCtx.createGain();
      videoGain.gain.value = videoVolume / 100;
      videoSrc.connect(videoGain);
      videoGain.connect(streamDest);

      if (audioFile) {
        const bgBuf = await audioCtx.decodeAudioData(await audioFile.arrayBuffer());
        const bgSrc = audioCtx.createBufferSource();
        bgSrc.buffer = bgBuf;
        bgSrc.loop = bgBuf.duration < (trimEnd - trimStart);
        const bgGain = audioCtx.createGain();
        bgGain.gain.value = bgVolume / 100;
        bgSrc.connect(bgGain);
        bgGain.connect(streamDest);
        bgSrc.start(0);
      }

      const canvasStream = canvas.captureStream(30);
      const finalStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...streamDest.stream.getAudioTracks(),
      ]);

      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus" : "video/webm";
      const recorder = new MediaRecorder(finalStream, { mimeType });
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      const cRect = containerRef.current?.getBoundingClientRect();
      const scaleY = cRect ? H / cRect.height : 1;

      const drawFrame = () => {
        if (exportCancelRef.current || video.currentTime >= trimEnd) {
          video.pause();
          recorder.stop();
          return;
        }

        ctx.filter = filterStr;
        ctx.globalAlpha = videoOpacity / 100;
        ctx.drawImage(video, 0, 0, W, H);
        ctx.globalAlpha = 1;
        ctx.filter = "none";

        const now = video.currentTime;
        const visible = textClips.filter((c) => now >= c.start && now <= c.end);

        for (const layer of visible) {
          ctx.save();
          ctx.globalAlpha = layer.opacity / 100;
          ctx.font = `bold ${Math.round(layer.size * scaleY)}px ${getCanvasFontName(layer.fontFamily)}`;
          ctx.fillStyle = layer.color;
          ctx.strokeStyle = "rgba(0,0,0,0.55)";
          ctx.lineWidth = Math.max(2, layer.size * scaleY * 0.08);
          const px = (layer.x / 100) * W;
          const py = (layer.y / 100) * H;
          ctx.strokeText(layer.text, px, py);
          ctx.fillText(layer.text, px, py);
          ctx.restore();
        }

        const prog = (video.currentTime - trimStart) / (trimEnd - trimStart);
        const pct = Math.round(prog * 95);
        // Throttle state updates to ~5 fps during export to avoid React churn.
        if (pct % 4 === 0) setExportProgress(pct);

        if (hasVFCB) {
          (video as any).requestVideoFrameCallback(drawFrame);
        } else {
          requestAnimationFrame(drawFrame);
        }
      };

      video.currentTime = trimStart;
      await new Promise<void>((r) => { video.onseeked = () => { video.onseeked = null; r(); }; });

      recorder.start(200);
      if (hasVFCB) {
        (video as any).requestVideoFrameCallback(drawFrame);
      } else {
        requestAnimationFrame(drawFrame);
      }
      await video.play();

      await new Promise<void>((r) => { recorder.onstop = () => r(); });

      if (exportCancelRef.current) {
        setIsExporting(false);
        setExportProgress(0);
        return;
      }

      const outBlob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(outBlob);
      setExportUrl(url);
      setExportProgress(100);
      setExportDone(true);
      setIsExporting(false);
    } catch (err) {
      console.error("QuickCut export error:", err);
      setIsExporting(false);
      setExportProgress(0);
    } finally {
      if (audioCtx) audioCtx.close().catch(() => {});
    }
  };

  const cancelExport = () => { exportCancelRef.current = true; };

  const downloadExport = () => {
    const a = document.createElement("a");
    a.href = exportUrl;
    a.download = `quickcut-${Date.now()}.webm`;
    a.click();
  };

  // ── Timeline render helpers ─────────────────────────────────────────────────
  const tickCount = Math.min(Math.ceil(duration * 2), 60); // max 60 ticks
  const ticks = Array.from({ length: tickCount }, (_, i) => (i / tickCount) * duration);

  // ── Export done screen ───────────────────────────────────────────────────────
  if (exportDone && exportUrl) {
    return (
      <div className={styles.root}>
        <div className={styles.doneRoot}>
          <div className={styles.topBar}>
            <button className={styles.iconBtn} onClick={() => { setExportDone(false); }} aria-label="Volver">
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <span className={styles.topLabel}>QUICKCUT</span>
            <div style={{ width: 36 }} />
          </div>
          <video src={exportUrl} controls playsInline className={styles.doneVideo} />
          <div className={styles.doneActions}>
            <button className={styles.btnGhost} onClick={() => { setExportDone(false); }}>
              ← Editar más
            </button>
            <button className={styles.btnAccent} onClick={downloadExport}>
              <Download size={13} strokeWidth={1.5} /> Guardar video
            </button>
          </div>
          <p className={styles.doneNote}>Formato WebM · procesado en tu dispositivo</p>
        </div>
      </div>
    );
  }

  // ── Main editor ─────────────────────────────────────────────────────────────
  return (
    <div className={styles.root}>

      {/* Export progress overlay */}
      {isExporting && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportCard}>
            <p className={styles.exportLabel}>Procesando video…</p>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${exportProgress}%` }} />
            </div>
            <p className={styles.exportPct}>{exportProgress}%</p>
            <button className={styles.cancelBtn} onClick={cancelExport}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className={styles.topBar}>
        <button className={styles.iconBtn} onClick={onBack} aria-label="Volver">
          <ChevronLeft size={18} strokeWidth={1.5} />
        </button>
        <span className={styles.topLabel}>QUICKCUT</span>
        <button
          className={`${styles.exportBtn} ${!hasChanges ? styles.exportBtnOff : ""}`}
          onClick={hasChanges ? startExport : undefined}
          disabled={!hasChanges}
          title={!hasChanges ? "Realiza algún cambio primero" : "Exportar video editado"}
        >
          Exportar
        </button>
      </div>

      {/* Hint bar */}
      <p className={styles.hintBar}>
        {activeTab === "trim" && "Arrastra los extremos del timeline para recortar. Arrastra la playhead para previsualizar."}
        {activeTab === "text" && "Añade clips de texto en la timeline. Arrástralos para posicionarlos en el tiempo."}
        {activeTab === "subtitles" && "Genera clips de texto automáticamente desde la transcripción en vivo de la grabación."}
        {activeTab === "filters" && "Aplica un filtro estético al video. Toca cualquiera para previsualizar."}
        {activeTab === "adjust" && "Ajusta brillo, contraste y saturación del video."}
        {activeTab === "audio" && "Controla el volumen del video o súbele música de fondo."}
      </p>

      {/* Video + overlays */}
      <div
        className={styles.videoWrapper}
        ref={containerRef}
        onPointerMove={onTextMove}
        onPointerUp={endTextDrag}
        onPointerCancel={endTextDrag}
        onClick={() => setSelectedClip(null)}
      >
        <video
          ref={videoRef}
          className={styles.editorVideo}
          style={{ filter: filterStr, opacity: videoOpacity / 100 }}
          playsInline
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
        />

        {/* Active text overlays at current time */}
        {activeClips.map((layer) => (
          <div
            key={layer.id}
            className={`${styles.textLayer} ${selectedClip === layer.id ? styles.textLayerSel : ""}`}
            style={{
              left: `${layer.x}%`,
              top: `${layer.y}%`,
              fontSize: `${layer.size}px`,
              color: layer.color,
              opacity: layer.opacity / 100,
              fontFamily: layer.fontFamily,
            }}
            onPointerDown={(e) => startTextDrag(e, layer.id, layer.x, layer.y)}
            onClick={(e) => { e.stopPropagation(); setSelectedClip(layer.id); }}
          >
            {layer.text}
          </div>
        ))}

        {/* Play overlay */}
        {!isPlaying && (
          <button className={styles.playOverlay} onClick={togglePlay} aria-label="Reproducir">
            <Play size={26} strokeWidth={1.4} />
          </button>
        )}
      </div>

      {/* ── NEW TIMELINE ───────────────────────────────────────────── */}
      <div className={styles.timelineSection} ref={timelineRef}>
        {/* Toolbar */}
        <div className={styles.timelineToolbar}>
          <div className={styles.timelineTimeInfo}>
            <span className={styles.timeCurrent}>{fmt(currentTime)}</span>
            <span className={styles.timeDivider}> / </span>
            <span className={styles.timeTotal}>{fmt(duration)}</span>
          </div>
          <div className={styles.timelineZoomControls}>
            <button className={styles.zoomBtn} onClick={() => setTimelineZoom((z) => Math.max(0.5, z - 0.25))}>-</button>
            <span className={styles.zoomVal}>{Math.round(timelineZoom * 100)}%</span>
            <button className={styles.zoomBtn} onClick={() => setTimelineZoom((z) => Math.min(3, z + 0.25))}>+</button>
          </div>
        </div>

        {/* Tracks container */}
        <div
          className={styles.timelineTracks}
          ref={timelineTrackRef}
          onPointerDown={onTimelinePointerDown}
          onPointerMove={onTimelinePointerMove}
          onPointerUp={onTimelinePointerUp}
          onPointerCancel={onTimelinePointerUp}
        >
          {/* Track ruler / ticks */}
          <div className={styles.rulerTrack}>
            {ticks.map((t, i) => (
              <div
                key={i}
                className={styles.rulerTick}
                style={{ left: `${(t / duration) * 100 * timelineZoom}%` }}
              >
                <span className={styles.rulerLabel}>{fmt(t)}</span>
              </div>
            ))}
          </div>

          {/* Video track */}
          <div className={styles.track}>
            <div className={styles.trackLabel}>VIDEO</div>
            <div className={styles.trackBody}>
              {/* Full duration bar */}
              <div className={styles.videoBar} style={{ width: `${100 * timelineZoom}%` }} />
              {/* Trim start dim */}
              <div className={styles.trimOut} style={{ width: `${(trimStart / duration) * 100 * timelineZoom}%`, left: 0 }} />
              {/* Trim end dim */}
              <div className={styles.trimOut} style={{ left: `${(trimEnd / duration) * 100 * timelineZoom}%`, right: 0 }} />
              {/* Active region */}
              <div
                className={styles.activeRegion}
                style={{
                  left: `${(trimStart / duration) * 100 * timelineZoom}%`,
                  width: `${((trimEnd - trimStart) / duration) * 100 * timelineZoom}%`,
                }}
              />
              {/* Trim handles */}
              {duration > 0 && (
                <>
                  <div
                    className={`${styles.handle} ${styles.handleL}`}
                    style={{ left: `${(trimStart / duration) * 100 * timelineZoom}%` }}
                    onPointerDown={(e) => startTrimDrag(e, "start")}
                    onPointerMove={(e) => onHandleMove(e, "start")}
                    onPointerUp={endTrimDrag}
                    onPointerCancel={endTrimDrag}
                  >
                    <span className={styles.handleTime}>{fmt(trimStart)}</span>
                  </div>
                  <div
                    className={`${styles.handle} ${styles.handleR}`}
                    style={{ left: `${(trimEnd / duration) * 100 * timelineZoom}%` }}
                    onPointerDown={(e) => startTrimDrag(e, "end")}
                    onPointerMove={(e) => onHandleMove(e, "end")}
                    onPointerUp={endTrimDrag}
                    onPointerCancel={endTrimDrag}
                  >
                    <span className={styles.handleTime}>{fmt(trimEnd)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Text clip tracks */}
          {textClips.length === 0 && (
            <div className={styles.emptyTrack}>
              <span className={styles.emptyTrackLabel}>Sin clips de texto</span>
            </div>
          )}
          {textClips.map((clip) => (
            <div key={clip.id} className={styles.track}>
              <div className={styles.trackLabel}>TXT</div>
              <div className={styles.trackBody}>
                <div
                  className={`${styles.textClip} ${selectedClip === clip.id ? styles.textClipSelected : ""}`}
                  style={{
                    left: `${(clip.start / duration) * 100 * timelineZoom}%`,
                    width: `${((clip.end - clip.start) / duration) * 100 * timelineZoom}%`,
                  }}
                  onPointerDown={(e) => startClipDrag(e, clip, "clip")}
                  onPointerMove={onClipDragMove}
                  onPointerUp={endClipDrag}
                  onPointerCancel={endClipDrag}
                  onClick={(e) => { e.stopPropagation(); setSelectedClip(clip.id); }}
                >
                  {/* Start handle */}
                  <div
                    className={styles.clipHandleL}
                    onPointerDown={(e) => startClipDrag(e, clip, "clipStart")}
                    onPointerMove={onClipDragMove}
                    onPointerUp={endClipDrag}
                    onPointerCancel={endClipDrag}
                  >
                    <GripHorizontal size={10} strokeWidth={1.5} />
                  </div>
                  {/* Clip content */}
                  <span className={styles.clipLabel}>{clip.text}</span>
                  <span className={styles.clipDuration}>{fmt(clip.end - clip.start)}</span>
                  {/* End handle */}
                  <div
                    className={styles.clipHandleR}
                    onPointerDown={(e) => startClipDrag(e, clip, "clipEnd")}
                    onPointerMove={onClipDragMove}
                    onPointerUp={endClipDrag}
                    onPointerCancel={endClipDrag}
                  >
                    <GripHorizontal size={10} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Playhead */}
          {duration > 0 && (
            <div
              className={styles.playhead}
              style={{ left: `${(currentTime / duration) * 100 * timelineZoom}%` }}
            >
              <div className={styles.playheadHead} />
              <div className={styles.playheadTime}>{fmt(currentTime)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Old duration row kept for compatibility */}
      <div className={styles.durationRow}>
        <span className={styles.durationClip}>Clip {fmt(trimEnd - trimStart)}</span>
        <span className={styles.durationTotal}>Total {fmt(duration)}</span>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        {([
          { id: "trim" as Tab, Icon: Scissors, label: "Trim" },
          { id: "text" as Tab, Icon: Type, label: "Texto" },
          { id: "subtitles" as Tab, Icon: Subtitles, label: "Subtítulos" },
          { id: "filters" as Tab, Icon: Clapperboard, label: "Filtros" },
          { id: "adjust" as Tab, Icon: Sliders, label: "Ajustar" },
          { id: "audio" as Tab, Icon: Music, label: "Audio" },
        ]).map(({ id, Icon, label }) => (
          <button
            key={id}
            className={`${styles.tabBtn} ${activeTab === id ? styles.tabBtnActive : ""}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={13} strokeWidth={1.5} /> {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={styles.tabContent}>

        {activeTab === "trim" && (
          <div className={styles.trimTab}>
            <div className={styles.trimStats}>
              <div className={styles.trimStat}>
                <span className={styles.trimStatLabel}>INICIO</span>
                <span className={styles.trimStatVal}>{fmt(trimStart)}</span>
              </div>
              <div className={styles.trimStat}>
                <span className={styles.trimStatLabel}>DURACIÓN</span>
                <span className={styles.trimStatVal}>{fmt(trimEnd - trimStart)}</span>
              </div>
              <div className={styles.trimStat}>
                <span className={styles.trimStatLabel}>FIN</span>
                <span className={styles.trimStatVal}>{fmt(trimEnd)}</span>
              </div>
            </div>
            <p className={styles.hint}>Arrastra los handles blancos en la pista VIDEO para definir inicio y fin.</p>
            <button className={styles.resetBtn} onClick={() => { setTrimStart(0); setTrimEnd(duration); }}>
              Restablecer
            </button>
          </div>
        )}

        {activeTab === "text" && (
          <div className={styles.textTab}>
            <button className={styles.addBtn} onClick={addTextClip}>
              <Plus size={12} strokeWidth={2} /> Añadir clip de texto
            </button>
            {textClips.length === 0 && (
              <p className={styles.hint}>Sin clips. Añade texto y arrástralo en la timeline para sincronizarlo.</p>
            )}

            {/* Text tool selector */}
            {selectedLayer && (
              <div className={styles.toolBar}>
                {([
                  { id: "move" as TextTool, Icon: MoveHorizontal, label: "Mover" },
                  { id: "font" as TextTool, Icon: TypeIcon, label: "Fuente" },
                  { id: "color" as TextTool, Icon: Palette, label: "Color" },
                  { id: "animation" as TextTool, Icon: Sparkles, label: "Animación" },
                ]).map(({ id, Icon, label }) => (
                  <button
                    key={id}
                    className={`${styles.toolBtn} ${textTool === id ? styles.toolBtnActive : ""}`}
                    onClick={() => setTextTool(id)}
                  >
                    <Icon size={12} strokeWidth={1.5} /> {label}
                  </button>
                ))}
              </div>
            )}

            {textClips.map((clip) => (
              <div
                key={clip.id}
                className={`${styles.layerRow} ${selectedClip === clip.id ? styles.layerRowSel : ""}`}
                onClick={() => setSelectedClip(clip.id)}
              >
                <span className={styles.layerPreview}>{clip.text}</span>
                <span className={styles.layerTime}>{fmt(clip.start)} – {fmt(clip.end)}</span>
                <button className={styles.removeBtn} onClick={(e) => { e.stopPropagation(); removeTextClip(clip.id); }}>
                  <Trash2 size={12} strokeWidth={1.5} />
                </button>
              </div>
            ))}

            {selectedLayer && (
              <div className={styles.textControls}>
                <input
                  className={styles.textInput}
                  value={selectedLayer.text}
                  onChange={(e) => updateTextClip(selectedLayer.id, { text: e.target.value })}
                  placeholder="Texto..."
                />

                {/* Font tool */}
                {textTool === "font" && (
                  <>
                    <div className={styles.fontGrid}>
                      {FONT_OPTIONS.map((font) => (
                        <button
                          key={font.value}
                          className={`${styles.fontBtn} ${selectedLayer.fontFamily === font.value ? styles.fontBtnActive : ""}`}
                          style={{ fontFamily: font.value }}
                          onClick={() => updateTextClip(selectedLayer.id, { fontFamily: font.value })}
                        >
                          {font.label}
                        </button>
                      ))}
                    </div>
                    <div className={styles.ctrlRow}>
                      <span className={styles.ctrlLabel}>Tamaño</span>
                      <input type="range" min={10} max={72} value={selectedLayer.size} className={styles.slider}
                        onChange={(e) => updateTextClip(selectedLayer.id, { size: +e.target.value })} />
                      <span className={styles.ctrlVal}>{selectedLayer.size}px</span>
                    </div>
                    <div className={styles.ctrlRow}>
                      <span className={styles.ctrlLabel}>Opacidad</span>
                      <input type="range" min={10} max={100} value={selectedLayer.opacity} className={styles.slider}
                        onChange={(e) => updateTextClip(selectedLayer.id, { opacity: +e.target.value })} />
                      <span className={styles.ctrlVal}>{selectedLayer.opacity}%</span>
                    </div>
                  </>
                )}

                {/* Color tool */}
                {textTool === "color" && (
                  <div className={styles.ctrlRow}>
                    <span className={styles.ctrlLabel}>Color</span>
                    <input type="color" value={selectedLayer.color} className={styles.colorPick}
                      onChange={(e) => updateTextClip(selectedLayer.id, { color: e.target.value })} />
                    <span className={styles.ctrlVal}>{selectedLayer.color}</span>
                  </div>
                )}

                {/* Animation tool */}
                {textTool === "animation" && (
                  <div className={styles.animGrid}>
                    {(["none", "fade", "slideUp", "typewriter"] as const).map((anim) => (
                      <button
                        key={anim}
                        className={`${styles.animBtn} ${selectedLayer.animation === anim ? styles.animBtnActive : ""}`}
                        onClick={() => updateTextClip(selectedLayer.id, { animation: anim })}
                      >
                        {anim === "none" && "—"}
                        {anim === "fade" && "Desvanecer"}
                        {anim === "slideUp" && "Subir"}
                        {anim === "typewriter" && "Máquina"}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "subtitles" && (
          <div className={styles.textTab}>
            {speechSegments.length === 0 ? (
              <div className={styles.subtitleEmpty}>
                <Captions size={28} strokeWidth={1} style={{ color: "rgba(255,255,255,0.15)" }} />
                <p className={styles.hint}>
                  No hay transcripción disponible. La transcripción se genera automáticamente durante la grabación si tu navegador lo soporta.
                </p>
              </div>
            ) : hasSubtitles ? (
              <div className={styles.subtitleDone}>
                <p className={styles.hint}>
                  Ya se generaron {textClips.length} clip(s) de subtítulo desde la transcripción en vivo.
                </p>
                <button className={styles.resetBtn} onClick={() => setTextClips([])}>
                  Borrar subtítulos
                </button>
              </div>
            ) : (
              <>
                <p className={styles.hint}>
                  Se detectaron <strong>{speechSegments.length}</strong> segmento(s) de voz transcritos durante la grabación.
                </p>
                <button className={styles.addBtn} onClick={generateSubtitlesFromSpeech}>
                  <Plus size={12} strokeWidth={2} /> Generar clips de subtítulos
                </button>
                <div className={styles.speechList}>
                  {speechSegments.map((seg, i) => (
                    <div key={i} className={styles.speechRow}>
                      <span className={styles.speechTime}>{fmt(seg.startMs / 1000)} – {fmt(seg.endMs / 1000)}</span>
                      <span className={styles.speechText}>{seg.text}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "filters" && (
          <div className={styles.filtersTab}>
            <div className={styles.filterGrid}>
              {FILTER_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  className={`${styles.filterCard} ${activeFilter === preset.id ? styles.filterCardActive : ""}`}
                  onClick={() => setActiveFilter(preset.id)}
                >
                  <div
                    className={styles.filterPreview}
                    style={{ filter: preset.filter }}
                  >
                    <span className={styles.filterIcon}>{preset.icon}</span>
                  </div>
                  <span className={styles.filterName}>{preset.name}</span>
                </button>
              ))}
            </div>
            <button className={styles.resetBtn} onClick={() => setActiveFilter("original")}>
              Restablecer filtro
            </button>
          </div>
        )}

        {activeTab === "adjust" && (
          <div className={styles.adjustTab}>
            <p className={styles.adjustGroupLabel}>LUZ Y COLOR</p>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Brillo</span>
              <input type="range" min={50} max={200} value={brightness} className={styles.slider}
                onChange={(e) => setBrightness(+e.target.value)} />
              <span className={styles.ctrlVal}>{brightness}%</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Contraste</span>
              <input type="range" min={50} max={200} value={contrast} className={styles.slider}
                onChange={(e) => setContrast(+e.target.value)} />
              <span className={styles.ctrlVal}>{contrast}%</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Saturación</span>
              <input type="range" min={0} max={200} value={saturation} className={styles.slider}
                onChange={(e) => setSaturation(+e.target.value)} />
              <span className={styles.ctrlVal}>{saturation}%</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Tono</span>
              <input type="range" min={0} max={360} value={hueRotate} className={styles.slider}
                onChange={(e) => setHueRotate(+e.target.value)} />
              <span className={styles.ctrlVal}>{hueRotate}°</span>
            </div>

            <p className={styles.adjustGroupLabel}>EFECTOS</p>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Desenfoque</span>
              <input type="range" min={0} max={10} step={0.5} value={blur} className={styles.slider}
                onChange={(e) => setBlur(+e.target.value)} />
              <span className={styles.ctrlVal}>{blur}px</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Sepia</span>
              <input type="range" min={0} max={100} value={sepia} className={styles.slider}
                onChange={(e) => setSepia(+e.target.value)} />
              <span className={styles.ctrlVal}>{sepia}%</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Grises</span>
              <input type="range" min={0} max={100} value={grayscale} className={styles.slider}
                onChange={(e) => setGrayscale(+e.target.value)} />
              <span className={styles.ctrlVal}>{grayscale}%</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Invertir</span>
              <input type="range" min={0} max={100} value={invert} className={styles.slider}
                onChange={(e) => setInvert(+e.target.value)} />
              <span className={styles.ctrlVal}>{invert}%</span>
            </div>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Opacidad</span>
              <input type="range" min={0} max={100} value={videoOpacity} className={styles.slider}
                onChange={(e) => setVideoOpacity(+e.target.value)} />
              <span className={styles.ctrlVal}>{videoOpacity}%</span>
            </div>

            <button className={styles.resetBtn}
              onClick={() => {
                setBrightness(100); setContrast(100); setSaturation(100);
                setHueRotate(0); setBlur(0); setSepia(0);
                setGrayscale(0); setInvert(0); setVideoOpacity(100);
              }}>
              Restablecer todo
            </button>
          </div>
        )}

        {activeTab === "audio" && (
          <div className={styles.audioTab}>
            <div className={styles.ctrlRow}>
              <span className={styles.ctrlLabel}>Video</span>
              <input type="range" min={0} max={100} value={videoVolume} className={styles.slider}
                onChange={(e) => setVideoVolume(+e.target.value)} />
              <span className={styles.ctrlVal}>{videoVolume}%</span>
            </div>
            <div className={styles.audioFileRow}>
              <span className={styles.ctrlLabel}>Música</span>
              <label className={styles.uploadBtn}>
                {audioFile ? audioFile.name.slice(0, 18) + "…" : "+ Subir audio"}
                <input type="file" accept="audio/*" style={{ display: "none" }}
                  onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)} />
              </label>
              {audioFile && (
                <button className={styles.removeBtn} onClick={() => setAudioFile(null)}>
                  <X size={12} strokeWidth={2} />
                </button>
              )}
            </div>
            {audioFile && (
              <div className={styles.ctrlRow}>
                <span className={styles.ctrlLabel}>Vol. música</span>
                <input type="range" min={0} max={100} value={bgVolume} className={styles.slider}
                  onChange={(e) => setBgVolume(+e.target.value)} />
                <span className={styles.ctrlVal}>{bgVolume}%</span>
              </div>
            )}
            {!audioFile && <p className={styles.hint}>Sube un audio para mezclar con el video durante la exportación.</p>}
          </div>
        )}

      </div>
    </div>
  );
}
